import { ParsedModifierGroup } from '../../../types/menu';

/**
 * N single-pick groups that the centre pane draws as one repeated-slot panel
 * instead of N rows.
 *
 * Two kinds, told apart by {@link RepeatedSlotSet.keepsPositions}:
 * - interchangeable slots, e.g. Manhattan Bagel's "Bakers Dozen Bagels" with
 *   `bagel_choice_1` … `bagel_choice_13`. Any bagel may sit in any slot, so
 *   the panel keeps like options together;
 * - per-unit slots, e.g. a Crowd Pleaser's `chili_dog_selection_1..4` beside
 *   `onion_options_selection_1..4`, where `_2` means "for chili dog 2". The
 *   panel must never move a pick from one slot to another.
 */
export interface RepeatedSlotSet {
  /** The shared name with the `_N` suffix removed: `bagel_choice`. */
  stem: string;
  /** The slots in index order: `groups[0]` is `<stem>_1`. */
  groups: ParsedModifierGroup[];
  /** Every slot must be filled (min 1), as opposed to "select up to 1". */
  isRequired: boolean;
  /**
   * Slot N belongs to unit N. True when the item carries another `_1..N`
   * family of the same length — the menu numbering several things per unit.
   */
  keepsPositions: boolean;
}

/**
 * Below this a set is not worth a panel: a "pick 2" pair reads fine as two
 * rows, and the panel's tiles + strip would take more room than they save.
 */
export const MIN_REPEATED_SLOTS = 3;

const SLOT_NAME_PATTERN = /^(.+)_(\d+)$/;

/** "Select 1" (required) or "select up to 1" (optional). */
const getSingleChoiceKind = (
  modGroup: ParsedModifierGroup
): 'required' | 'optional' | null => {
  if (modGroup.maximumSelections !== 1) return null;
  if (modGroup.minimumSelections === 1) return 'required';
  if (!modGroup.minimumSelections) return 'optional';
  return null;
};

// `uniqueName` first: the front end's own min=max>1 split
// (menuUnification.utils.ts) writes `<constraint>_<n>` there and keeps the bare
// constraint name in `name`. Brand-menu constraints that are separate on the
// menu carry the suffix in `id` as well, so the fallback covers them.
const getSlotName = (modGroup: ParsedModifierGroup) =>
  modGroup.uniqueName || String(modGroup.id);

const getOptionSignature = (modGroup: ParsedModifierGroup) =>
  Object.keys(modGroup.menuItems || {})
    .sort()
    .join('\u0000');

// A slot box holds one name. An option that opens further groups (a chili dog
// with its own onion and cheese choices) has nowhere to put them.
const hasNestedOptions = (modGroup: ParsedModifierGroup) =>
  Object.values(modGroup.menuItems || {}).some(
    (option) => Object.keys(option?.modifierGroups ?? {}).length > 0
  );

/**
 * Finds the repeated-slot sets among ONE level of an item's modifier groups.
 *
 * A set is groups that all:
 * 1. are single-pick of the same kind — all "select 1" or all "select up to 1";
 * 2. are named `<stem>_<n>` with the same stem, the indices running 1..N with
 *    no gap or repeat;
 * 3. offer exactly the same options, none of which opens further groups;
 * 4. number at least {@link MIN_REPEATED_SLOTS}.
 *
 * A stem that breaks any rule for any of its groups yields no set at all, so
 * the panel never takes over a set it would have to draw half of. Names alone
 * are not enough (two sauce slots can share a stem and differ in options), and
 * options alone are not enough (two unrelated groups can list the same ones),
 * which is why rules 2 and 3 are both required.
 *
 * Every `_1..N` family on the level counts towards `keepsPositions`, including
 * ones that are not a set themselves: a chili-dog family that stays as rows
 * still makes its onion family per-dog.
 */
export const findRepeatedSlotSets = (
  modGroups: ParsedModifierGroup[]
): RepeatedSlotSet[] => {
  const byStem = new Map<
    string,
    { index: number; modGroup: ParsedModifierGroup }[]
  >();

  modGroups.forEach((modGroup) => {
    const match = SLOT_NAME_PATTERN.exec(getSlotName(modGroup));
    if (!match) return;
    const [, stem, index] = match;
    const members = byStem.get(stem) || [];
    members.push({ index: Number(index), modGroup });
    byStem.set(stem, members);
  });

  // Every well-formed `_1..N` family, by stem, with its length.
  const families = new Map<
    string,
    { index: number; modGroup: ParsedModifierGroup }[]
  >();
  byStem.forEach((members, stem) => {
    const sorted = [...members].sort((a, b) => a.index - b.index);
    if (sorted.every(({ index }, position) => index === position + 1)) {
      families.set(stem, sorted);
    }
  });

  const sets: RepeatedSlotSet[] = [];
  families.forEach((sorted, stem) => {
    if (sorted.length < MIN_REPEATED_SLOTS) return;

    const kind = getSingleChoiceKind(sorted[0].modGroup);
    if (!kind) return;
    if (!sorted.every(({ modGroup }) => getSingleChoiceKind(modGroup) === kind))
      return;

    const signature = getOptionSignature(sorted[0].modGroup);
    if (!signature) return;
    if (
      !sorted.every(
        ({ modGroup }) => getOptionSignature(modGroup) === signature
      )
    )
      return;
    if (sorted.some(({ modGroup }) => hasNestedOptions(modGroup))) return;

    const keepsPositions = Array.from(families).some(
      ([otherStem, other]) =>
        otherStem !== stem && other.length === sorted.length
    );

    sets.push({
      stem,
      groups: sorted.map(({ modGroup }) => modGroup),
      isRequired: kind === 'required',
      keepsPositions,
    });
  });

  return sets;
};

/**
 * The one intervention line a repeated-slot write sends:
 * `Bakers Dozen Bagels: 6 Plain, 4 Everything, 2 Sesame, 1 of 13 empty`.
 * Options are listed in the order they first appear in `slots`, which the
 * panel keeps grouped, so the line reads the way the strip does. Empty slots
 * are only mentioned when there are some.
 */
export const describeRepeatedSlots = (
  itemName: string,
  slots: (string | undefined)[]
): string => {
  const counts = new Map<string, number>();
  let empty = 0;
  slots.forEach((name) => {
    if (!name) {
      empty += 1;
      return;
    }
    counts.set(name, (counts.get(name) || 0) + 1);
  });
  const parts = Array.from(counts, ([name, count]) => `${count} ${name}`);
  if (empty) parts.push(`${empty} of ${slots.length} empty`);
  return `${itemName}: ${parts.length ? parts.join(', ') : 'cleared'}`;
};

/** One slot's value: the selected option's `itemId`, or undefined if empty. */
export type SlotValue = string | undefined;

const countSlots = (slots: SlotValue[]) => {
  const counts = new Map<string, number>();
  slots.forEach((id) => {
    if (id) counts.set(id, (counts.get(id) || 0) + 1);
  });
  return counts;
};

/**
 * Lays `counts` out the way the panel's strip always reads: like options
 * together, in `optionOrder` (the tile order), empties last. Slots are
 * interchangeable, so this is what readback and the POS ticket print too.
 * An id missing from `optionOrder` keeps its place after the known ones
 * rather than being dropped.
 */
export const layoutSlots = (
  counts: Map<string, number>,
  optionOrder: string[],
  slotCount: number
): SlotValue[] => {
  const order = [
    ...optionOrder,
    ...Array.from(counts.keys()).filter((id) => !optionOrder.includes(id)),
  ];
  const laidOut: SlotValue[] = order.flatMap((id) =>
    Array(counts.get(id) || 0).fill(id)
  );
  return [
    ...laidOut,
    ...Array(Math.max(0, slotCount - laidOut.length)).fill(undefined),
  ].slice(0, slotCount);
};

/** `slots` regrouped, unchanged in content. */
export const groupSlots = (slots: SlotValue[], optionOrder: string[]) =>
  layoutSlots(countSlots(slots), optionOrder, slots.length);

/**
 * Every operation below works on positions first, then regroups unless the
 * set keeps positions (per-unit slots, where slot N is unit N's). Regrouped,
 * the result is the same as counting and laying out afresh.
 */
const settle = (
  slots: SlotValue[],
  optionOrder: string[],
  keepPositions: boolean
) => (keepPositions ? slots : groupSlots(slots, optionOrder));

/** One more `id` in the first empty slot. Null when no slot is empty. */
export const addToSlots = (
  slots: SlotValue[],
  id: string,
  optionOrder: string[],
  keepPositions = false
): SlotValue[] | null => {
  const empty = slots.findIndex((slot) => !slot);
  if (empty < 0) return null;
  return settle(
    slots.map((slot, index) => (index === empty ? id : slot)),
    optionOrder,
    keepPositions
  );
};

/** The last slot holding `id` emptied. Null when `id` is in no slot. */
export const removeFromSlots = (
  slots: SlotValue[],
  id: string,
  optionOrder: string[],
  keepPositions = false
): SlotValue[] | null => {
  const last = slots.lastIndexOf(id);
  if (last < 0) return null;
  return settle(
    slots.map((slot, index) => (index === last ? undefined : slot)),
    optionOrder,
    keepPositions
  );
};

/**
 * The slots at `indexes` set to `id` — or emptied, when `id` is undefined
 * (clearing a per-unit optional slot).
 */
export const paintSlots = (
  slots: SlotValue[],
  indexes: number[],
  id: SlotValue,
  optionOrder: string[],
  keepPositions = false
): SlotValue[] =>
  settle(
    slots.map((slot, index) => (indexes.includes(index) ? id : slot)),
    optionOrder,
    keepPositions
  );

/** Every empty slot set to `id`. */
export const fillEmptySlots = (
  slots: SlotValue[],
  id: string,
  optionOrder: string[],
  keepPositions = false
): SlotValue[] =>
  settle(
    slots.map((slot) => slot || id),
    optionOrder,
    keepPositions
  );
